import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, CheckCircle2, Info } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';

const TIME_SLOTS = [
  '9:00 AM - 10:00 AM',
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '12:00 PM - 1:00 PM',
  '2:00 PM - 3:00 PM',
  '3:00 PM - 4:00 PM',
  '4:00 PM - 5:00 PM',
  '5:00 PM - 6:00 PM',
  '6:00 PM - 7:00 PM',
];

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const BookAppointment = () => {
  const BACKEND_URL = API_BASE;

  const [date, setDate] = useState();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    age: '',
    gender: '',
    service: '',
    doctor: '',
    timeSlot: '',
    healthConcern: '',
    consent: false,
  });

  // Fetch doctors + departments together
  useEffect(() => {
    const load = async () => {
      try {
        const [docRes, deptRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/doctors/synced`),
          fetch(`${BACKEND_URL}/api/departments`),
        ]);
        if (docRes.ok) setDoctors(await docRes.json());
        if (deptRes.ok) setDepartments(await deptRes.json());
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    load();
  }, [BACKEND_URL]);

  // Doctors filtered by selected department
  const filteredDoctors = useMemo(() => {
    if (!formData.service) return doctors;
    return doctors.filter(
      (d) =>
        (d.specialization || '').trim().toLowerCase() ===
        formData.service.trim().toLowerCase(),
    );
  }, [doctors, formData.service]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // If department changes, reset preferred doctor since list changes
      if (field === 'service') next.doctor = '';
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.phone ||
      !formData.age ||
      !formData.gender ||
      !formData.service ||
      !date ||
      !formData.timeSlot
    ) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.consent) {
      toast.error('Please agree to be contacted by Healthwrx to proceed');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        age: parseInt(formData.age, 10),
        gender: formData.gender,
        service: formData.service,
        doctor: formData.doctor || null,
        date: format(date, 'yyyy-MM-dd'),
        timeSlot: formData.timeSlot,
        healthConcern: formData.healthConcern || null,
        consent: true,
      };

      const response = await fetch(`${BACKEND_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit request');
      }

      setIsSubmitted(true);
      toast.success('Appointment request received');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(typeof error.message === 'string' ? error.message : 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center font-[Montserrat]">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-0 shadow-2xl" data-testid="appointment-success-card">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Thank you. Your appointment request has been received.
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Our team will contact you shortly on WhatsApp or phone to confirm your appointment.
              </p>
              <p className="text-sm text-gray-500">
                We&apos;ll reach you at <strong>{formData.phone}</strong>
              </p>
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    phone: '',
                    age: '',
                    gender: '',
                    service: '',
                    doctor: '',
                    timeSlot: '',
                    healthConcern: '',
                    consent: false,
                  });
                  setDate(undefined);
                }}
                variant="outline"
                className="mt-8"
                data-testid="submit-another-btn"
              >
                Submit another request
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50 font-[Montserrat]">
      {/* Hero */}
      <section className="py-12 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Request Appointment</h1>
          <p className="text-base md:text-lg text-gray-100">
            Share your preferred slot. Our team will confirm with you on WhatsApp or phone.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto border-0 shadow-xl">
            <CardContent className="p-6 md:p-12">
              {/* Notice */}
              <div className="flex items-start gap-3 p-4 mb-8 bg-amber-50 border border-amber-200 rounded-lg">
                <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  This is an appointment <strong>request</strong>, not an instant booking. Our reception
                  team will contact you on WhatsApp or phone to confirm a suitable date and time.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      required
                      className="mt-2"
                      data-testid="appt-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700">
                      Phone / WhatsApp Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="mt-2"
                      data-testid="appt-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="age" className="text-gray-700">
                      Age <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="age"
                      type="number"
                      min="0"
                      max="120"
                      value={formData.age}
                      onChange={(e) => handleChange('age', e.target.value)}
                      placeholder="e.g. 32"
                      required
                      className="mt-2"
                      data-testid="appt-age-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="gender" className="text-gray-700">
                      Gender <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => handleChange('gender', value)}
                    >
                      <SelectTrigger className="mt-2" data-testid="appt-gender-select">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map((g) => (
                          <SelectItem key={g.value} value={g.value}>
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Appointment Details */}
                <div className="pt-6 border-t">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Appointment Details</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="service" className="text-gray-700">
                      Service Type / Department <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.service}
                      onValueChange={(value) => handleChange('service', value)}
                    >
                      <SelectTrigger className="mt-2" data-testid="appt-service-select">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="doctor" className="text-gray-700">
                      Preferred Doctor (optional)
                    </Label>
                    <Select
                      value={formData.doctor}
                      onValueChange={(value) => handleChange('doctor', value)}
                      disabled={!formData.service}
                    >
                      <SelectTrigger className="mt-2" data-testid="appt-doctor-select">
                        <SelectValue
                          placeholder={formData.service ? 'Select doctor' : 'Pick a department first'}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredDoctors.length === 0 ? (
                          <div className="px-2 py-1.5 text-sm text-gray-500">No doctors listed</div>
                        ) : (
                          filteredDoctors.map((d) => (
                            <SelectItem key={d.id} value={d.name}>
                              {d.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-700">
                      Preferred Date <span className="text-red-500">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-2"
                          data-testid="appt-date-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, 'PPP') : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="timeSlot" className="text-gray-700">
                      Preferred Time Slot <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.timeSlot}
                      onValueChange={(value) => handleChange('timeSlot', value)}
                    >
                      <SelectTrigger className="mt-2" data-testid="appt-timeslot-select">
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="healthConcern" className="text-gray-700">
                    Short Health Concern (optional)
                  </Label>
                  <Textarea
                    id="healthConcern"
                    value={formData.healthConcern}
                    onChange={(e) => handleChange('healthConcern', e.target.value)}
                    placeholder="Briefly describe what brings you in (optional)"
                    rows={3}
                    className="mt-2"
                    data-testid="appt-concern-input"
                  />
                </div>

                {/* Consent */}
                <div className="pt-6 border-t">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.consent}
                      onCheckedChange={(v) => handleChange('consent', Boolean(v))}
                      className="mt-1"
                      data-testid="appt-consent-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      I agree to be contacted by Healthwrx via phone or WhatsApp regarding my appointment request.{' '}
                      <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full bg-[#14D1A2] hover:bg-[#10B88A] text-white text-lg h-14 disabled:opacity-60"
                    data-testid="appt-submit-btn"
                  >
                    {submitting ? 'Submitting…' : 'Submit Request'}
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4">
                    Awaiting Confirmation — our team typically reaches out within a few hours during clinic time.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default BookAppointment;
